/**
 * OPAC 予約数キャッシュ用 Zustand ストア (メモリのみ)
 *
 * BookCard 展開時に URL をまとめて渡すと、未取得分をバッチ API で
 * 引いてキャッシュする。同じ URL に対する多重フェッチはガードする。
 * キャッシュには :data:`CACHE_TTL_MS` の有効期限があり、期限切れは再取得される。
 */

import { create } from 'zustand'

import { fetchLibraryStatsBatch } from '@/api/services/library-stats'
import type { LibraryStatsItem, LibraryStatsResult } from '@/api/types/library-stats'

/** キャッシュ有効期間 (ミリ秒)。予約数は 1 時間以内なら再取得しない。 */
export const CACHE_TTL_MS = 60 * 60 * 1000

type LibraryStatsState = {
  /** URL → 結果キャッシュ */
  byUrl: Record<string, LibraryStatsResult>
  /** URL → 取得完了時刻 (epoch ms)。TTL 判定に使う。 */
  fetchedAtByUrl: Record<string, number>
  /** 取得中の URL 集合 (Set の代わりに Record を使う) */
  fetchingUrls: Record<string, true>

  /** items のうち未取得・未進行中・TTL 期限切れのものを裏でバッチ取得する (fire-and-forget) */
  ensureStats: (items: LibraryStatsItem[]) => void
  /** ストアを空にする (region 切り替え時などに呼ぶ) */
  reset: () => void
}

export const useLibraryStatsStore = create<LibraryStatsState>()((set, get) => ({
  byUrl: {},
  fetchedAtByUrl: {},
  fetchingUrls: {},

  ensureStats: (items) => {
    if (items.length === 0) return
    const { byUrl, fetchedAtByUrl, fetchingUrls } = get()
    const now = Date.now()

    // URL ベースで重複除去し、未取得 / TTL 切れ / 未進行のみに絞る
    const seen = new Set<string>()
    const pending: LibraryStatsItem[] = []
    for (const item of items) {
      if (!item.url) continue
      if (seen.has(item.url)) continue
      seen.add(item.url)
      const cached = byUrl[item.url]
      const fetchedAt = fetchedAtByUrl[item.url]
      // 1 時間以内に取得済みなら再フェッチしない (成功 / error / unsupported 問わず TTL 適用)
      if (cached && fetchedAt !== undefined && now - fetchedAt < CACHE_TTL_MS) continue
      if (fetchingUrls[item.url]) continue
      pending.push(item)
    }
    if (pending.length === 0) return

    // 進行中フラグを立てる
    const nextFetching = { ...fetchingUrls }
    for (const item of pending) {
      nextFetching[item.url] = true
    }
    set({ fetchingUrls: nextFetching })

    // 非同期でバッチ取得。完了後に結果をマージし、進行中フラグを外す。
    void (async () => {
      try {
        const results = await fetchLibraryStatsBatch(pending)
        const completedAt = Date.now()
        const { byUrl: currentByUrl, fetchedAtByUrl: currentFetchedAt, fetchingUrls: currentFetching } = get()
        const nextByUrl = { ...currentByUrl }
        const nextFetchedAt = { ...currentFetchedAt }
        const clearedFetching = { ...currentFetching }
        for (const result of results) {
          nextByUrl[result.url] = result
          nextFetchedAt[result.url] = completedAt
          delete clearedFetching[result.url]
        }
        // 結果に含まれなかった URL のフラグも念のためクリア
        for (const item of pending) {
          delete clearedFetching[item.url]
        }
        set({
          byUrl: nextByUrl,
          fetchedAtByUrl: nextFetchedAt,
          fetchingUrls: clearedFetching,
        })
      } catch (error) {
        // 失敗時は進行中フラグを解除するのみ。ユーザにはUIで「取得失敗」として見えれば十分。
        if (import.meta.env.DEV) {
          console.warn('[library-stats] batch fetch failed:', error)
        }
        const { fetchingUrls: currentFetching } = get()
        const clearedFetching = { ...currentFetching }
        for (const item of pending) {
          delete clearedFetching[item.url]
        }
        set({ fetchingUrls: clearedFetching })
      }
    })()
  },

  reset: () => set({ byUrl: {}, fetchedAtByUrl: {}, fetchingUrls: {} }),
}))
